import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Users, DollarSign, Home, Mail, LogOut, Check, X, 
  Upload, Image, Settings, Link2, CalendarSync, Plus, Edit, Trash2,
  Phone, CreditCard, Banknote, QrCode, Eye, ChevronLeft, ChevronRight,
  CalendarDays, MessageCircle, Filter, Edit3
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Button } from '../components/ui/button';
import { format, differenceInDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO, isWithinInterval, addDays } from 'date-fns';
import { apiErrorMessage } from '../lib/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout, loading: authLoading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [activeTab, setActiveTab] = useState('bookings');
  const [loading, setLoading] = useState(true);
  
  // Booking Filters
  const [bookingFilter, setBookingFilter] = useState('all');
  const [selectedBookingDetail, setSelectedBookingDetail] = useState(null);
  
  // POS Booking State
  const [showPOSModal, setShowPOSModal] = useState(false);
  const [posForm, setPosForm] = useState({
    room_id: '', check_in: null, check_out: null, guests: 1,
    guest_name: '', guest_phone: '', guest_email: '',
    payment_method: 'cash', amount_paid: 0, notes: '', source: 'walk_in'
  });
  const [roomBookedDates, setRoomBookedDates] = useState([]);
  
  // Image Upload State
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState([]);
  
  // Room Edit State
  const [editingRoom, setEditingRoom] = useState(null);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [imagePickerTarget, setImagePickerTarget] = useState(null);
  const [isNewRoom, setIsNewRoom] = useState(false);
  
  // Site Content State
  const [siteContent, setSiteContent] = useState({ homepage: { hero_images: [] } });
  
  // Calendar State
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarBookings, setCalendarBookings] = useState([]);
  const [selectedCalendarBooking, setSelectedCalendarBooking] = useState(null);
  
  // OTA Links
  const [otaLinks, setOtaLinks] = useState({ airbnb: '', booking_com: '' });

  // Change Password
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/admin/login');
      } else if (!isAdmin) {
        navigate('/dashboard');
        toast.error('Admin access required');
      } else {
        fetchData();
      }
    }
  }, [user, authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (activeTab === 'calendar' && user) {
      fetchCalendarBookings();
    }
  }, [activeTab, calendarDate, user]);

  const fetchData = async () => {
    try {
      const [bookingsRes, contactsRes, roomsRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/rooms`),
      ]);
      setBookings(bookingsRes.data);
      setContacts(contactsRes.data);
      setRooms(roomsRes.data);
      
      try {
        const [uploadsRes, contentRes, otaRes] = await Promise.all([
          axios.get(`${API}/admin/uploads`),
          axios.get(`${API}/admin/content`),
          axios.get(`${API}/ota-links`)
        ]);
        setUploads(uploadsRes.data || []);
        setSiteContent(contentRes.data || { homepage: { hero_images: [] } });
        setOtaLinks(otaRes.data || { airbnb: '', booking_com: '' });
      } catch (e) {
        console.log('Optional data fetch:', e);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarBookings = async () => {
    try {
      const month = calendarDate.getMonth() + 1;
      const year = calendarDate.getFullYear();
      const response = await axios.get(`${API}/admin/bookings/calendar?month=${month}&year=${year}`);
      setCalendarBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Error fetching calendar:', error);
    }
  };

  const handleStatusUpdate = async (bookingId, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status?status=${status}`);
      toast.success('Status updated');
      fetchData();
      fetchCalendarBookings();
      setSelectedBookingDetail(null);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  // Filter bookings
  const filteredBookings = bookings.filter(b => {
    if (bookingFilter === 'all') return true;
    if (bookingFilter === 'pending') return b.status === 'pending';
    if (bookingFilter === 'confirmed') return b.status === 'confirmed';
    if (bookingFilter === 'cancelled') return b.status === 'cancelled';
    if (bookingFilter === 'paid') return b.payment_status === 'paid';
    if (bookingFilter === 'unpaid') return b.payment_status !== 'paid';
    return true;
  });

  // Get booked dates for a specific room (for POS date picker)
  const getBookedDatesForRoom = (roomId) => {
    if (!roomId) return [];
    const roomBookings = bookings.filter(b => b.room_id === roomId && b.status !== 'cancelled');
    const bookedDates = [];
    
    roomBookings.forEach(booking => {
      try {
        const checkIn = parseISO(booking.check_in);
        const checkOut = parseISO(booking.check_out);
        const days = eachDayOfInterval({ start: checkIn, end: addDays(checkOut, -1) });
        days.forEach(day => {
          bookedDates.push({
            date: day,
            booking: booking
          });
        });
      } catch (e) {
        console.log('Date parse error:', e);
      }
    });
    
    return bookedDates;
  };

  // Count how many units are booked for a specific date
  const getBookedUnitsCountForDate = (date, roomId) => {
    if (!roomId) return 0;
    const bookedDates = getBookedDatesForRoom(roomId);
    return bookedDates.filter(bd => isSameDay(bd.date, date)).length;
  };

  // Check if ALL units are booked for the selected room on a specific date
  const isDateFullyBooked = (date, roomId) => {
    if (!roomId) return false;
    const room = rooms.find(r => r.id === roomId);
    const totalUnits = room?.total_units || 1;
    const bookedCount = getBookedUnitsCountForDate(date, roomId);
    return bookedCount >= totalUnits;
  };

  // Check if a date has ANY booking (for highlighting)
  const isDatePartiallyBooked = (date, roomId) => {
    if (!roomId) return false;
    const bookedDates = getBookedDatesForRoom(roomId);
    return bookedDates.some(bd => isSameDay(bd.date, date));
  };

  // Get booking info for a specific date and room
  const getBookingForDate = (date, roomId) => {
    if (!roomId) return null;
    const bookedDates = getBookedDatesForRoom(roomId);
    const found = bookedDates.find(bd => isSameDay(bd.date, date));
    return found?.booking || null;
  };

  // POS Booking Functions
  const handlePOSSubmit = async () => {
    if (!posForm.room_id || !posForm.check_in || !posForm.check_out || !posForm.guest_name || !posForm.guest_phone) {
      toast.error('Please fill all required fields'); return;
    }
    try {
      await axios.post(`${API}/admin/pos/booking`, {
        ...posForm,
        check_in: format(posForm.check_in, 'yyyy-MM-dd'),
        check_out: format(posForm.check_out, 'yyyy-MM-dd'),
      });
      toast.success('Booking created!');
      setShowPOSModal(false);
      setPosForm({ room_id: '', check_in: null, check_out: null, guests: 1, guest_name: '', guest_phone: '', guest_email: '', payment_method: 'cash', amount_paid: 0, notes: '', source: 'walk_in' });
      fetchData();
      fetchCalendarBookings();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to create booking'));
    }
  };

  // Image functions
  const handleImageUpload = async (e, category = 'general') => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const urls = [];
    let failed = 0;
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      try {
        const response = await axios.post(`${API}/admin/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        urls.push(response.data.url);
        setUploads(prev => [response.data, ...prev]);
      } catch {
        failed++;
      }
    }
    setUploading(false);
    if (urls.length) toast.success(`${urls.length} image${urls.length > 1 ? 's' : ''} uploaded!`);
    if (failed) toast.error(`${failed} upload${failed > 1 ? 's' : ''} failed`);
    return urls.length === 1 ? urls[0] : urls.length > 1 ? urls : null;
  };

  const handleDeleteUpload = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await axios.delete(`${API}/admin/uploads/${imageId}`);
      setUploads(uploads.filter(u => u.id !== imageId));
      toast.success('Image deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  // Room Functions
  const createNewRoom = () => {
    setIsNewRoom(true);
    setEditingRoom({
      id: null, name: '', slug: '', category: 'heritage', description: '', short_description: '',
      price_per_night: 10000, original_price: null, max_guests: 2, amenities: ['Wi-Fi', 'AC', 'Attached Bathroom'],
      images: [], features: [], is_featured: false, is_available: true
    });
  };

  const handleRoomSave = async () => {
    if (!editingRoom) return;
    if (!editingRoom.name || !editingRoom.slug || editingRoom.images.length === 0) {
      toast.error('Please fill name, slug, and add at least one image'); return;
    }
    try {
      if (isNewRoom) {
        await axios.post(`${API}/rooms`, {
          name: editingRoom.name, slug: editingRoom.slug, category: editingRoom.category,
          description: editingRoom.description, short_description: editingRoom.short_description,
          price_per_night: editingRoom.price_per_night, original_price: editingRoom.original_price, max_guests: editingRoom.max_guests,
          amenities: editingRoom.amenities, images: editingRoom.images, features: editingRoom.features,
          is_featured: editingRoom.is_featured, is_available: editingRoom.is_available
        });
        toast.success('Room created!');
      } else {
        await axios.put(`${API}/admin/rooms/${editingRoom.id}/details`, {
          name: editingRoom.name, short_description: editingRoom.short_description,
          description: editingRoom.description, price_per_night: editingRoom.price_per_night,
          original_price: editingRoom.original_price,
          max_guests: editingRoom.max_guests, amenities: editingRoom.amenities,
          features: editingRoom.features, is_featured: editingRoom.is_featured,
          is_available: editingRoom.is_available, category: editingRoom.category,
        });
        await axios.put(`${API}/admin/rooms/${editingRoom.id}/images`, editingRoom.images);
        toast.success('Room updated!');
      }
      setEditingRoom(null);
      setIsNewRoom(false);
      fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Save failed'));
    }
  };

  const handleRoomDelete = async () => {
    if (!editingRoom || isNewRoom) return;
    if (!window.confirm(`Delete "${editingRoom.name}"?`)) return;
    try {
      await axios.delete(`${API}/rooms/${editingRoom.id}`);
      toast.success('Room deleted');
      setEditingRoom(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete room');
    }
  };

  const addImageToRoom = (url) => {
    if (!editingRoom) return;
    setEditingRoom(prev => ({ ...prev, images: [...prev.images, getFullImageUrl(url)] }));
    // don't close picker here — batch uploads call this multiple times; picker closes via onClose
  };

  const closeImagePicker = () => setShowImagePicker(false);

  const removeImageFromRoom = (index) => {
    if (!editingRoom) return;
    const imgs = [...editingRoom.images];
    imgs.splice(index, 1);
    setEditingRoom({ ...editingRoom, images: imgs });
  };

  const moveImage = (index, dir) => {
    if (!editingRoom) return;
    const imgs = [...editingRoom.images];
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= imgs.length) return;
    [imgs[index], imgs[newIdx]] = [imgs[newIdx], imgs[index]];
    setEditingRoom({ ...editingRoom, images: imgs });
  };

  // Hero Images
  const handleHeroImagesUpdate = async () => {
    try {
      await axios.put(`${API}/admin/content/homepage`, { ...siteContent.homepage });
      toast.success('Homepage updated!');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const addHeroImage = (url) => {
    setSiteContent({
      ...siteContent,
      homepage: { ...siteContent.homepage, hero_images: [...(siteContent.homepage?.hero_images || []), getFullImageUrl(url)] }
    });
    setShowImagePicker(false);
  };

  const removeHeroImage = (idx) => {
    const imgs = [...(siteContent.homepage?.hero_images || [])];
    imgs.splice(idx, 1);
    setSiteContent({ ...siteContent, homepage: { ...siteContent.homepage, hero_images: imgs } });
  };

  // OTA
  const handleOTALinksUpdate = async () => {
    try {
      await axios.put(`${API}/admin/ota-links`, otaLinks);
      toast.success('OTA links updated!');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleChangePassword = async () => {
    if (!pwForm.current || !pwForm.next) {
      toast.error('Please fill in all fields');
      return;
    }
    if (pwForm.next.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    setPwLoading(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: pwForm.current,
        new_password: pwForm.next,
      });
      toast.success('Password changed successfully');
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to change password'));
    } finally {
      setPwLoading(false);
    }
  };

  const getCalendarUrl = (roomId) => `${process.env.REACT_APP_BACKEND_URL}/api/calendar/export/${roomId}.ics`;
  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };

  // Calendar helpers
  const getBookingsForDate = (date) => {
    return calendarBookings.filter(booking => {
      try {
        const checkIn = parseISO(booking.check_in);
        const checkOut = addDays(parseISO(booking.check_out), -1); // checkout day is not occupied
        return isWithinInterval(date, { start: checkIn, end: checkOut }) || isSameDay(date, checkIn);
      } catch {
        return false;
      }
    });
  };

  const getRoomColor = (roomId) => {
    const colors = [
      { bg: 'bg-red-500', text: 'text-red-500' },
      { bg: 'bg-blue-500', text: 'text-blue-500' },
      { bg: 'bg-green-500', text: 'text-green-500' },
      { bg: 'bg-purple-500', text: 'text-purple-500' },
      { bg: 'bg-orange-500', text: 'text-orange-500' },
      { bg: 'bg-pink-500', text: 'text-pink-500' },
      { bg: 'bg-cyan-500', text: 'text-cyan-500' },
    ];
    // Safety check: if rooms not loaded or roomId not found, return default
    if (!rooms || rooms.length === 0) return { bg: 'bg-gray-500', text: 'text-gray-500' };
    const index = rooms.findIndex(r => r.id === roomId);
    if (index === -1) return { bg: 'bg-gray-500', text: 'text-gray-500' };
    return colors[index % colors.length];
  };

  // Contact helpers
  const getWhatsAppLink = (phone, name) => {
    const cleanPhone = phone?.replace(/[^0-9]/g, '') || '';
    const msg = encodeURIComponent(`Hi ${name}, this is Rudragarh Resort regarding your booking.`);
    return `https://wa.me/${cleanPhone}?text=${msg}`;
  };

  // POS calculations
  const posNights = posForm.check_in && posForm.check_out ? differenceInDays(posForm.check_out, posForm.check_in) : 0;
  const selectedPosRoom = rooms.find(r => r.id === posForm.room_id);
  const posTotal = selectedPosRoom ? posNights * selectedPosRoom.price_per_night : 0;

  // Calendar
  const monthStart = startOfMonth(calendarDate);
  const monthEnd = endOfMonth(calendarDate);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  if (authLoading || loading) {
    return <div className="min-h-screen pt-20 flex items-center justify-center"><div className="w-12 h-12 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const stats = [
    { label: 'Total', value: bookings.length, icon: Calendar, color: 'text-blue-500' },
    { label: 'Pending', value: bookings.filter(b => b.status === 'pending').length, icon: Users, color: 'text-yellow-500' },
    { label: 'Confirmed', value: bookings.filter(b => b.status === 'confirmed').length, icon: Check, color: 'text-green-500' },
    { label: 'Revenue', value: `₹${bookings.filter(b => b.payment_status === 'paid').reduce((sum, b) => sum + b.total_amount, 0).toLocaleString()}`, icon: DollarSign, color: 'text-gold-500' },
  ];

  // Booking Detail Popup
  const BookingDetailPopup = ({ booking, onClose }) => {
    if (!booking) return null;
    const room = rooms.find(r => r.id === booking.room_id);
    
    return (
      <div className="fixed inset-0 z-50 bg-void/95 flex items-center justify-center p-4" onClick={onClose}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-void-paper border border-gold-500/20 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="font-heading text-2xl">Booking Details</h2>
              <p className="text-foreground/50 text-sm">ID: {booking.id?.slice(0, 8)}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10"><X size={24} /></button>
          </div>

          {/* Status Badges */}
          <div className="flex flex-wrap gap-2 mb-6">
            <span className={`px-3 py-1 text-sm font-medium ${
              booking.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
              booking.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
              'bg-yellow-500/20 text-yellow-400'
            }`}>{booking.status?.toUpperCase()}</span>
            <span className={`px-3 py-1 text-sm font-medium ${
              booking.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' :
              booking.payment_status === 'partial' ? 'bg-orange-500/20 text-orange-400' :
              'bg-red-500/20 text-red-400'
            }`}>{booking.payment_status?.toUpperCase()}</span>
            {booking.source && booking.source !== 'website' && (
              <span className="px-3 py-1 text-sm font-medium bg-blue-500/20 text-blue-400">{booking.source?.toUpperCase()}</span>
            )}
          </div>

          {/* Room Info */}
          <div className="bg-void-subtle p-4 mb-6">
            <div className="flex items-center gap-4">
              {room && <img src={room.images?.[0]} alt={room.name} className="w-24 h-16 object-cover" />}
              <div>
                <h3 className="font-heading text-xl text-gold-500">{booking.room_name}</h3>
                <p className="text-foreground/60 text-sm">{room?.category}</p>
              </div>
            </div>
          </div>

          {/* Guest Info with Contact Buttons */}
          <div className="mb-6">
            <h4 className="font-heading text-lg mb-3 text-gold-500">Guest Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-foreground/50 text-sm">Name</p>
                <p className="font-medium text-lg">{booking.guest_name}</p>
              </div>
              <div>
                <p className="text-foreground/50 text-sm">Phone</p>
                <p className="font-medium text-lg">{booking.guest_phone}</p>
              </div>
              <div>
                <p className="text-foreground/50 text-sm">Email</p>
                <p className="font-medium">{booking.guest_email || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-foreground/50 text-sm">Guests</p>
                <p className="font-medium">{booking.guests} people</p>
              </div>
            </div>

            {/* Contact Buttons */}
            <div className="flex flex-wrap gap-3 mt-4">
              <a href={`tel:${booking.guest_phone}`} className="flex items-center gap-2 px-4 py-2 bg-gold-500 text-black hover:bg-gold-400 transition-all">
                <Phone size={18} /> Call
              </a>
              <a href={getWhatsAppLink(booking.guest_phone, booking.guest_name)} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white hover:bg-green-500 transition-all">
                <MessageCircle size={18} /> WhatsApp
              </a>
              {booking.guest_email && (
                <a href={`mailto:${booking.guest_email}?subject=Rudragarh Booking ${booking.id?.slice(0, 8)}`}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-500 transition-all">
                  <Mail size={18} /> Email
                </a>
              )}
            </div>
          </div>

          {/* Booking Details */}
          <div className="mb-6">
            <h4 className="font-heading text-lg mb-3 text-gold-500">Stay Details</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-void-subtle p-3">
                <p className="text-foreground/50 text-xs">Check-in</p>
                <p className="font-heading text-lg">{booking.check_in}</p>
              </div>
              <div className="bg-void-subtle p-3">
                <p className="text-foreground/50 text-xs">Check-out</p>
                <p className="font-heading text-lg">{booking.check_out}</p>
              </div>
              <div className="bg-void-subtle p-3">
                <p className="text-foreground/50 text-xs">Nights</p>
                <p className="font-heading text-lg">{differenceInDays(parseISO(booking.check_out), parseISO(booking.check_in))}</p>
              </div>
              <div className="bg-void-subtle p-3">
                <p className="text-foreground/50 text-xs">Booked On</p>
                <p className="font-heading text-sm">{new Date(booking.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div className="mb-6">
            <h4 className="font-heading text-lg mb-3 text-gold-500">Payment</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-void-subtle p-3">
                <p className="text-foreground/50 text-xs">Total Amount</p>
                <p className="font-heading text-xl text-gold-500">₹{booking.total_amount?.toLocaleString()}</p>
              </div>
              {booking.amount_paid !== undefined && (
                <div className="bg-void-subtle p-3">
                  <p className="text-foreground/50 text-xs">Paid</p>
                  <p className="font-heading text-xl text-green-500">₹{booking.amount_paid?.toLocaleString()}</p>
                </div>
              )}
              {booking.balance_due > 0 && (
                <div className="bg-void-subtle p-3">
                  <p className="text-foreground/50 text-xs">Balance Due</p>
                  <p className="font-heading text-xl text-red-500">₹{booking.balance_due?.toLocaleString()}</p>
                </div>
              )}
            </div>
            {booking.payment_method && (
              <p className="text-foreground/50 text-sm mt-2">Payment Method: <span className="text-foreground">{booking.payment_method}</span></p>
            )}
          </div>

          {/* Special Requests */}
          {booking.special_requests && (
            <div className="mb-6">
              <h4 className="font-heading text-lg mb-2 text-gold-500">Special Requests</h4>
              <p className="text-foreground/70 bg-void-subtle p-3">{booking.special_requests}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-gold-500/10">
            {booking.status === 'pending' && (
              <>
                <button onClick={() => handleStatusUpdate(booking.id, 'confirmed')} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white hover:bg-green-500">
                  <Check size={18} /> Confirm
                </button>
                <button onClick={() => handleStatusUpdate(booking.id, 'cancelled')} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white hover:bg-red-500">
                  <X size={18} /> Cancel
                </button>
              </>
            )}
            {booking.status === 'confirmed' && (
              <button onClick={() => handleStatusUpdate(booking.id, 'cancelled')} className="flex items-center gap-2 px-4 py-2 bg-red-600/20 text-red-400 hover:bg-red-600 hover:text-white">
                <X size={18} /> Cancel Booking
              </button>
            )}
          </div>
        </motion.div>
      </div>
    );
  };

  // Image Picker Modal
  const ImagePickerModal = ({ onSelect, onClose }) => (
    <div className="fixed inset-0 z-[60] bg-void/95 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-void-paper border border-gold-500/20 p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-heading text-xl">Select Image</h3>
          <button onClick={onClose}><X size={24} /></button>
        </div>
        <div className="mb-6 p-4 border border-dashed border-gold-500/30 text-center">
          <label className="cursor-pointer">
            <input type="file" accept="image/*" multiple className="hidden" onChange={async (e) => { const result = await handleImageUpload(e, 'room'); if (result) { const urls = Array.isArray(result) ? result : [result]; urls.forEach(u => onSelect(u)); } }} disabled={uploading} />
            <div className="flex flex-col items-center gap-2 text-foreground/60 hover:text-gold-500">
              <Upload size={32} /><span>{uploading ? 'Uploading...' : 'Upload New (select multiple)'}</span>
            </div>
          </label>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {uploads.map((u) => (
            <button key={u.id} onClick={() => onSelect(u.url)} className="aspect-square overflow-hidden border-2 border-transparent hover:border-gold-500">
              <img src={getFullImageUrl(u.url)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
        <div className="mt-6 p-4 bg-void-subtle">
          <label className="text-sm text-foreground/60 mb-2 block">Or paste URL</label>
          <div className="flex gap-2">
            <input type="url" id="ext-url" placeholder="https://..." className="flex-1 input-default px-4" />
            <button onClick={() => { const u = document.getElementById('ext-url').value; if (u) onSelect(u); }} className="btn-primary">Add</button>
          </div>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen pt-20" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto container-padding section-padding">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <span className="overline">Admin Panel</span>
            <h1 className="text-3xl md:text-4xl font-heading"><span className="text-gold-500">Rudragarh</span> Dashboard</h1>
          </div>
          <div className="flex gap-3">
            <button onClick={() => navigate('/cms')} className="btn-outline flex items-center gap-2"><Edit3 size={18} /> Content Manager</button>
            <button onClick={() => setShowPOSModal(true)} className="btn-primary flex items-center gap-2"><Plus size={18} /> POS Booking</button>
            <button onClick={handleLogout} className="btn-ghost"><LogOut size={18} /></button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {stats.map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="card-glass p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 bg-white/5 ${s.color}`}><s.icon size={20} /></div>
                <div><p className="text-foreground/60 text-xs">{s.label}</p><p className="font-heading text-xl">{s.value}</p></div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'bookings', label: 'Bookings', icon: Calendar },
            { id: 'calendar', label: 'Calendar', icon: CalendarDays },
            { id: 'rooms', label: 'Rooms', icon: Home },
            { id: 'media', label: 'Media', icon: Image },
            { id: 'homepage', label: 'Homepage', icon: Eye },
            { id: 'sync', label: 'Sync', icon: CalendarSync },
            { id: 'settings', label: 'Settings', icon: Settings },
            { id: 'contacts', label: 'Inquiries', icon: Mail },
          ].map((t) => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest whitespace-nowrap ${
                activeTab === t.id ? 'bg-gold-500 text-black' : 'text-foreground/60 hover:text-gold-500'
              }`} data-testid={`admin-tab-${t.id}`}>
              <t.icon size={16} /><span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>

        {/* BOOKINGS TAB - Grid Cards with Filters */}
        {activeTab === 'bookings' && (
          <div className="space-y-6">
            {/* Filter Buttons - Always Color Coded */}
            <div className="flex flex-wrap gap-2 mb-6">
              {[
                { id: 'all', label: 'All', bgColor: 'bg-gray-600', hoverColor: 'hover:bg-gray-500', textColor: 'text-white' },
                { id: 'pending', label: 'Pending', bgColor: 'bg-yellow-500', hoverColor: 'hover:bg-yellow-400', textColor: 'text-black' },
                { id: 'confirmed', label: 'Confirmed', bgColor: 'bg-green-500', hoverColor: 'hover:bg-green-400', textColor: 'text-white' },
                { id: 'cancelled', label: 'Cancelled', bgColor: 'bg-red-500', hoverColor: 'hover:bg-red-400', textColor: 'text-white' },
                { id: 'paid', label: 'Paid', bgColor: 'bg-emerald-500', hoverColor: 'hover:bg-emerald-400', textColor: 'text-white' },
                { id: 'unpaid', label: 'Unpaid', bgColor: 'bg-orange-500', hoverColor: 'hover:bg-orange-400', textColor: 'text-black' },
              ].map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setBookingFilter(filter.id)}
                  className={`px-4 py-2 text-sm font-medium transition-all flex items-center gap-2 ${filter.bgColor} ${filter.hoverColor} ${filter.textColor} ${
                    bookingFilter === filter.id 
                      ? 'ring-2 ring-white ring-offset-2 ring-offset-void scale-105' 
                      : 'opacity-80 hover:opacity-100'
                  }`}
                >
                  <Filter size={14} />
                  {filter.label}
                  <span className={`text-xs ${filter.textColor === 'text-black' ? 'opacity-60' : 'opacity-80'}`}>
                    ({filter.id === 'all' ? bookings.length :
                      filter.id === 'pending' ? bookings.filter(b => b.status === 'pending').length :
                      filter.id === 'confirmed' ? bookings.filter(b => b.status === 'confirmed').length :
                      filter.id === 'cancelled' ? bookings.filter(b => b.status === 'cancelled').length :
                      filter.id === 'paid' ? bookings.filter(b => b.payment_status === 'paid').length :
                      bookings.filter(b => b.payment_status !== 'paid').length})
                  </span>
                </button>
              ))}
            </div>

            {/* Booking Cards Grid */}
            {filteredBookings.length === 0 ? (
              <div className="card-glass p-12 text-center"><p className="text-foreground/60">No bookings found</p></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredBookings.map((booking) => {
                  const room = rooms.find(r => r.id === booking.room_id);
                  return (
                    <motion.div
                      key={booking.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="card-glass overflow-hidden cursor-pointer hover:border-gold-500/50 transition-all"
                      onClick={() => setSelectedBookingDetail(booking)}
                    >
                      {/* Room Image Header */}
                      <div className="relative h-32 bg-void-subtle">
                        {room?.images?.[0] && (
                          <img src={room.images[0]} alt="" className="w-full h-full object-cover" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-void to-transparent" />
                        <div className="absolute bottom-2 left-2 right-2">
                          <h3 className="font-heading text-lg truncate">{booking.room_name}</h3>
                          {booking.unit_number && room?.total_units > 1 && (
                            <span className="text-xs text-gold-400">Unit #{booking.unit_number} of {room.total_units}</span>
                          )}
                        </div>
                        {/* Status Badge */}
                        <div className="absolute top-2 right-2 flex gap-1">
                          <span className={`px-2 py-0.5 text-xs font-medium ${
                            booking.status === 'confirmed' ? 'bg-green-500 text-black' :
                            booking.status === 'cancelled' ? 'bg-red-500 text-white' :
                            'bg-yellow-500 text-black'
                          }`}>{booking.status}</span>
                        </div>
                      </div>

                      {/* Booking Info */}
                      <div className="p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <Users size={16} className="text-gold-500" />
                          <span className="font-medium">{booking.guest_name}</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm text-foreground/60">
                          <Calendar size={14} />
                          <span>{booking.check_in} → {booking.check_out}</span>
                        </div>

                        <div className="flex items-center gap-2 text-sm text-foreground/60">
                          <Phone size={14} />
                          <span>{booking.guest_phone}</span>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-gold-500/10">
                          <span className="text-gold-500 font-heading text-lg">₹{booking.total_amount?.toLocaleString()}</span>
                          <span className={`px-2 py-0.5 text-xs ${
                            booking.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' :
                            booking.payment_status === 'partial' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>{booking.payment_status}</span>
                        </div>

                        {booking.source && booking.source !== 'website' && (
                          <div className="text-xs text-blue-400 bg-blue-500/10 px-2 py-1 inline-block">
                            via {booking.source}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* CALENDAR TAB */}
        {activeTab === 'calendar' && (
          <div className="space-y-6">
            {/* Loading State */}
            {rooms.length === 0 ? (
              <div className="card-glass p-12 text-center">
                <p className="text-foreground/60">Loading rooms...</p>
              </div>
            ) : (
              <>
                {/* Room Legend */}
                <div className="card-glass p-4 flex flex-wrap gap-4">
                  {rooms.map((room) => (
                    <div key={room.id} className="flex items-center gap-2">
                      <div className={`w-4 h-4 ${getRoomColor(room.id).bg}`}></div>
                      <span className="text-sm">{room.name}</span>
                    </div>
                  ))}
                </div>

            {/* Calendar */}
            <div className="card-glass p-6">
              <div className="flex items-center justify-between mb-6">
                <button onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1))} className="p-2 hover:bg-white/10"><ChevronLeft size={24} /></button>
                <h2 className="font-heading text-2xl">{format(calendarDate, 'MMMM yyyy')}</h2>
                <button onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1))} className="p-2 hover:bg-white/10"><ChevronRight size={24} /></button>
              </div>

              <div className="grid grid-cols-7 gap-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                  <div key={d} className="text-center text-sm text-foreground/60 py-2 font-medium">{d}</div>
                ))}
                {Array.from({ length: monthStart.getDay() }).map((_, i) => <div key={`e-${i}`} />)}
                {calendarDays.map(day => {
                  const dayBookings = getBookingsForDate(day);
                  const isToday = isSameDay(day, new Date());
                  return (
                    <div
                      key={day.toISOString()}
                      className={`min-h-[80px] p-1 border border-gold-500/10 cursor-pointer hover:bg-white/5 ${isToday ? 'bg-gold-500/10 border-gold-500/30' : ''}`}
                      onClick={() => dayBookings.length > 0 && setSelectedCalendarBooking(dayBookings[0])}
                    >
                      <span className={`text-sm ${isToday ? 'text-gold-500 font-bold' : 'text-foreground/60'}`}>{format(day, 'd')}</span>
                      <div className="space-y-1 mt-1">
                        {dayBookings.slice(0, 3).map((b, i) => (
                          <div
                            key={`${b.id}-${i}`}
                            className={`${getRoomColor(b.room_id).bg} text-[10px] text-white px-1 py-0.5 truncate cursor-pointer hover:opacity-80`}
                            onClick={(e) => { e.stopPropagation(); setSelectedCalendarBooking(b); }}
                            title={`${b.room_name} - ${b.guest_name}`}
                          >
                            {b.guest_name?.split(' ')[0]}
                          </div>
                        ))}
                        {dayBookings.length > 3 && <span className="text-[10px] text-foreground/50">+{dayBookings.length - 3} more</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
              </>
            )}
          </div>
        )}

        {/* ROOMS TAB */}
        {activeTab === 'rooms' && (
          <div className="space-y-6">
            <div className="flex justify-end">
              <button onClick={createNewRoom} className="btn-primary flex items-center gap-2"><Plus size={18} /> Add New Room</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rooms.map((room) => (
                <div key={room.id} className="card-glass overflow-hidden cursor-pointer group" onClick={() => { setIsNewRoom(false); setEditingRoom({...room}); }}>
                  <div className="relative">
                    <img src={room.images?.[0]} alt={room.name} className="w-full h-40 object-cover" />
                    <div className="absolute inset-0 bg-void/60 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
                      <span className="btn-primary text-sm py-2">Edit</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-lg">{room.name}</h3>
                    <p className="text-foreground/60 text-xs uppercase">{room.category}</p>
                    <p className="text-gold-500 font-heading mt-1">₹{room.price_per_night?.toLocaleString()}/night</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MEDIA TAB */}
        {activeTab === 'media' && (
          <div className="space-y-6">
            <div className="card-glass p-6">
              <h3 className="font-heading text-xl mb-4">Upload Images</h3>
              <div className="flex flex-wrap gap-4">
                {['room', 'gallery', 'banner', 'general'].map((cat) => (
                  <label key={cat} className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, cat)} disabled={uploading} />
                    <div className="flex items-center gap-2 px-4 py-2 bg-void-subtle border border-gold-500/20 hover:border-gold-500">
                      <Upload size={18} className="text-gold-500" /><span className="text-sm capitalize">{cat}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {uploads.map((u) => (
                <div key={u.id} className="relative group">
                  <img src={getFullImageUrl(u.url)} alt="" className="w-full aspect-square object-cover" />
                  <div className="absolute inset-0 bg-void/80 opacity-0 group-hover:opacity-100 transition-all flex flex-col items-center justify-center gap-2 p-2">
                    <span className="text-xs text-gold-500">{u.category}</span>
                    <button onClick={() => copyToClipboard(getFullImageUrl(u.url))} className="text-xs px-2 py-1 bg-gold-500 text-black w-full">Copy</button>
                    <button onClick={() => handleDeleteUpload(u.id)} className="text-xs px-2 py-1 bg-red-500 text-white w-full">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* HOMEPAGE TAB */}
        {activeTab === 'homepage' && (
          <div className="card-glass p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-heading text-xl">Homepage Hero Images</h3>
              <button onClick={() => { setImagePickerTarget('hero'); setShowImagePicker(true); }} className="btn-outline text-sm py-2"><Plus size={16} className="mr-1" />Add</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {(siteContent.homepage?.hero_images || []).map((img, i) => (
                <div key={i} className="relative group">
                  <img src={img} alt="" className="w-full aspect-video object-cover" />
                  <div className="absolute inset-0 bg-void/60 opacity-0 group-hover:opacity-100 flex items-center justify-center">
                    <button onClick={() => removeHeroImage(i)} className="p-2 bg-red-500 text-white"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
            {(siteContent.homepage?.hero_images || []).length > 0 && (
              <button onClick={handleHeroImagesUpdate} className="btn-primary mt-4">Save</button>
            )}
          </div>
        )}

        {/* SYNC TAB */}
        {activeTab === 'sync' && (
          <div className="space-y-6">
            {/* Info Banner */}
            <div className="bg-blue-500/10 border border-blue-500/20 p-4">
              <h4 className="text-blue-300 font-medium mb-2">📅 Smart iCal Sync</h4>
              <p className="text-sm text-foreground/60 mb-2">
                We have <strong>two types</strong> of iCal feeds:
              </p>
              <ul className="text-sm text-foreground/60 space-y-1 ml-4">
                <li>• <strong>Internal (Google Calendar)</strong> - Shows all individual bookings with guest details</li>
                <li>• <strong>OTA (Airbnb/Booking.com)</strong> - Shows "Not Available" only when room type is fully booked</li>
              </ul>
              <p className="text-sm text-foreground/60 mt-2">
                <span className="text-yellow-400">⚠️ Safety Buffer:</span> OTA feed blocks dates when only 1 unit remains (prevents double-booking during sync delay)
              </p>
            </div>

            {/* All Rooms */}
            <div className="card-glass p-6">
              <h3 className="font-heading text-xl mb-4">All Rooms Feed</h3>
              <div className="p-4 bg-void-subtle flex justify-between items-center">
                <div>
                  <h4 className="font-heading">Combined Calendar (Internal)</h4>
                  <p className="text-xs text-foreground/50 font-mono">{process.env.REACT_APP_BACKEND_URL}/api/calendar/export/all.ics</p>
                </div>
                <button onClick={() => copyToClipboard(`${process.env.REACT_APP_BACKEND_URL}/api/calendar/export/all.ics`)} className="btn-outline text-sm py-2">Copy</button>
              </div>
            </div>

            {/* Per Room */}
            <div className="card-glass p-6">
              <h3 className="font-heading text-xl mb-4">Per Room Type</h3>
              <div className="space-y-4">
                {rooms.map((r) => (
                  <div key={r.id} className="p-4 bg-void-subtle border border-gold-500/10">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-heading">{r.name}</h4>
                        <p className="text-xs text-foreground/50">{r.total_units || 1} units</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {/* Internal URL */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5">Internal</span>
                        <p className="text-xs text-foreground/50 font-mono flex-1 truncate">{process.env.REACT_APP_BACKEND_URL}/api/calendar/export/{r.id}.ics?mode=internal</p>
                        <button onClick={() => copyToClipboard(`${process.env.REACT_APP_BACKEND_URL}/api/calendar/export/${r.id}.ics?mode=internal`)} className="text-xs text-gold-500 hover:underline">Copy</button>
                      </div>
                      {/* OTA URL */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5">OTA</span>
                        <p className="text-xs text-foreground/50 font-mono flex-1 truncate">{process.env.REACT_APP_BACKEND_URL}/api/calendar/export/{r.id}.ics?mode=ota</p>
                        <button onClick={() => copyToClipboard(`${process.env.REACT_APP_BACKEND_URL}/api/calendar/export/${r.id}.ics?mode=ota`)} className="text-xs text-gold-500 hover:underline">Copy</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Instructions */}
            <div className="card-glass p-6">
              <h3 className="font-heading text-xl mb-4">Setup Instructions</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-4 bg-void-subtle">
                  <h4 className="font-heading text-red-400 mb-2">Airbnb</h4>
                  <ol className="text-sm text-foreground/60 space-y-1 list-decimal ml-4">
                    <li>Go to your Airbnb listing</li>
                    <li>Click Calendar → Availability</li>
                    <li>Click "Import Calendar"</li>
                    <li>Paste the <strong>OTA URL</strong> for that room type</li>
                    <li>Name it "Rudragarh Website"</li>
                  </ol>
                </div>
                <div className="p-4 bg-void-subtle">
                  <h4 className="font-heading text-blue-400 mb-2">Booking.com</h4>
                  <ol className="text-sm text-foreground/60 space-y-1 list-decimal ml-4">
                    <li>Go to Booking.com Extranet</li>
                    <li>Click Rates & Availability → Calendar</li>
                    <li>Click "Sync Calendar"</li>
                    <li>Paste the <strong>OTA URL</strong> for that room type</li>
                    <li>Save and sync</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SETTINGS TAB */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* OTA Links */}
            <div className="card-glass p-6">
              <h3 className="font-heading text-xl mb-4">OTA Links</h3>
              <div className="space-y-4">
                <div><label className="text-sm text-foreground/60 mb-2 block">Airbnb</label><input type="url" value={otaLinks.airbnb || ''} onChange={(e) => setOtaLinks({ ...otaLinks, airbnb: e.target.value })} className="w-full input-default px-4" /></div>
                <div><label className="text-sm text-foreground/60 mb-2 block">Booking.com</label><input type="url" value={otaLinks.booking_com || ''} onChange={(e) => setOtaLinks({ ...otaLinks, booking_com: e.target.value })} className="w-full input-default px-4" /></div>
                <button onClick={handleOTALinksUpdate} className="btn-primary">Save</button>
              </div>
            </div>

            {/* Change Password */}
            <div className="card-glass p-6">
              <h3 className="font-heading text-xl mb-1">Change Password</h3>
              <p className="text-sm text-foreground/50 mb-5">Update your admin account password.</p>
              <div className="space-y-4 max-w-sm">
                <div>
                  <label className="text-sm text-foreground/60 mb-2 block">Current Password</label>
                  <input
                    type="password"
                    value={pwForm.current}
                    onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                    className="w-full input-default px-4"
                    placeholder="••••••••"
                  />
                </div>
                <div>
                  <label className="text-sm text-foreground/60 mb-2 block">New Password</label>
                  <input
                    type="password"
                    value={pwForm.next}
                    onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
                    className="w-full input-default px-4"
                    placeholder="Min. 8 characters"
                  />
                </div>
                <div>
                  <label className="text-sm text-foreground/60 mb-2 block">Confirm New Password</label>
                  <input
                    type="password"
                    value={pwForm.confirm}
                    onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                    className="w-full input-default px-4"
                    placeholder="••••••••"
                  />
                </div>
                <button
                  onClick={handleChangePassword}
                  disabled={pwLoading}
                  className="btn-primary disabled:opacity-50"
                >
                  {pwLoading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CONTACTS TAB */}
        {activeTab === 'contacts' && (
          <div className="space-y-4">
            {contacts.length === 0 ? (
              <div className="card-glass p-12 text-center"><p className="text-foreground/60">No inquiries</p></div>
            ) : contacts.map((c) => (
              <div key={c.id} className="card-glass p-6">
                <div className="flex items-center gap-3 mb-2"><h3 className="font-heading text-lg">{c.name}</h3><span className="text-xs text-foreground/40">{new Date(c.created_at).toLocaleDateString()}</span></div>
                <p className="text-sm text-foreground/60">{c.email}</p>
                <p className="text-gold-500">{c.subject}</p>
                <p className="text-foreground/70">{c.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODALS */}
      {/* POS Modal */}
      {showPOSModal && (
        <div className="fixed inset-0 z-50 bg-void/90 flex items-center justify-center p-4" onClick={() => setShowPOSModal(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-void-paper border border-gold-500/20 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-2xl mb-6">POS <span className="text-gold-500">Booking</span></h2>
            <div className="space-y-4">
              <div><label className="text-sm text-foreground/60 mb-2 block">Room *</label>
                <select value={posForm.room_id} onChange={(e) => setPosForm({ ...posForm, room_id: e.target.value, check_in: null, check_out: null })} className="w-full input-default px-4">
                  <option value="">Select a room first...</option>
                  {rooms.map((r) => <option key={r.id} value={r.id}>{r.name} - ₹{r.price_per_night?.toLocaleString()}</option>)}
                </select>
              </div>
              
              {/* Existing Bookings for Selected Room */}
              {posForm.room_id && (() => {
                const selectedRoom = rooms.find(r => r.id === posForm.room_id);
                const totalUnits = selectedRoom?.total_units || 1;
                const roomBookings = bookings.filter(b => b.room_id === posForm.room_id && b.status !== 'cancelled');
                
                return (
                  <div className="p-3 bg-void-subtle border border-gold-500/10">
                    {/* Room Inventory Info */}
                    <div className="flex items-center justify-between mb-3 pb-2 border-b border-gold-500/10">
                      <span className="text-sm text-foreground/70">Total Units:</span>
                      <span className="text-gold-400 font-heading text-lg">{totalUnits} {totalUnits === 1 ? 'Unit' : 'Units'}</span>
                    </div>
                    
                    <p className="text-xs text-foreground/50 mb-2 flex items-center gap-1">
                      <CalendarDays size={12} /> Current bookings for this room type:
                    </p>
                    {roomBookings.length === 0 ? (
                      <p className="text-sm text-green-400">✓ No existing bookings - all {totalUnits} units available!</p>
                    ) : (
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {roomBookings
                          .sort((a, b) => new Date(a.check_in) - new Date(b.check_in))
                          .map((b) => (
                            <div key={b.id} className="flex items-center justify-between text-xs bg-red-500/10 px-2 py-1 rounded">
                              <div className="flex items-center gap-2">
                                {b.unit_number && totalUnits > 1 && (
                                  <span className="bg-gold-500/20 text-gold-400 px-1.5 py-0.5 rounded text-xs">Unit #{b.unit_number}</span>
                                )}
                                <span className="text-red-400 font-medium">{b.check_in} → {b.check_out}</span>
                              </div>
                              <span className="text-foreground/70">{b.guest_name} ({b.guest_phone})</span>
                            </div>
                          ))
                        }
                      </div>
                    )}
                    
                    {/* Hint about multiple units */}
                    {totalUnits > 1 && roomBookings.length > 0 && roomBookings.length < totalUnits && (
                      <p className="text-xs text-green-400 mt-2 pt-2 border-t border-gold-500/10">
                        ✓ {totalUnits - roomBookings.length} of {totalUnits} units may still be available (depends on dates)
                      </p>
                    )}
                  </div>
                );
              })()}

              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-sm text-foreground/60 mb-2 block">Check-in *</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start bg-white/5 border-white/10" disabled={!posForm.room_id}>
                        <Calendar className="mr-2 h-4 w-4 text-gold-500" />
                        {posForm.check_in ? format(posForm.check_in, 'MMM d, yyyy') : (posForm.room_id ? 'Select date' : 'Select room first')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-void-paper border-gold-500/20">
                      <CalendarComponent 
                        mode="single" 
                        selected={posForm.check_in} 
                        onSelect={(d) => setPosForm({ ...posForm, check_in: d, check_out: null })}
                        disabled={(date) => {
                          // Disable past dates
                          if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
                          // Disable only if ALL units are booked
                          return isDateFullyBooked(date, posForm.room_id);
                        }}
                        modifiers={{
                          fullyBooked: (date) => isDateFullyBooked(date, posForm.room_id),
                          partiallyBooked: (date) => isDatePartiallyBooked(date, posForm.room_id) && !isDateFullyBooked(date, posForm.room_id)
                        }}
                        modifiersStyles={{
                          fullyBooked: { backgroundColor: 'rgba(239, 68, 68, 0.4)', color: '#fca5a5', textDecoration: 'line-through' },
                          partiallyBooked: { backgroundColor: 'rgba(251, 191, 36, 0.3)', color: '#fcd34d' }
                        }}
                      />
                      {posForm.room_id && (
                        <div className="p-2 border-t border-gold-500/10 text-xs text-foreground/50 space-y-1">
                          <div><span className="inline-block w-3 h-3 bg-red-500/40 mr-1"></span> = All units booked (disabled)</div>
                          <div><span className="inline-block w-3 h-3 bg-yellow-500/30 mr-1"></span> = Some units booked (available)</div>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>
                <div><label className="text-sm text-foreground/60 mb-2 block">Check-out *</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start bg-white/5 border-white/10" disabled={!posForm.check_in}>
                        <Calendar className="mr-2 h-4 w-4 text-gold-500" />
                        {posForm.check_out ? format(posForm.check_out, 'MMM d, yyyy') : (posForm.check_in ? 'Select date' : 'Select check-in first')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-void-paper border-gold-500/20">
                      <CalendarComponent 
                        mode="single" 
                        selected={posForm.check_out} 
                        onSelect={(d) => setPosForm({ ...posForm, check_out: d })} 
                        disabled={(date) => {
                          // Must be after check-in
                          if (date <= (posForm.check_in || new Date())) return true;
                          // Check if any date between check_in and this date is fully booked
                          if (posForm.check_in) {
                            const daysToCheck = eachDayOfInterval({ start: addDays(posForm.check_in, 1), end: addDays(date, -1) });
                            if (daysToCheck.some(d => isDateFullyBooked(d, posForm.room_id))) return true;
                          }
                          return false;
                        }}
                        modifiers={{
                          fullyBooked: (date) => isDateFullyBooked(date, posForm.room_id),
                          partiallyBooked: (date) => isDatePartiallyBooked(date, posForm.room_id) && !isDateFullyBooked(date, posForm.room_id)
                        }}
                        modifiersStyles={{
                          fullyBooked: { backgroundColor: 'rgba(239, 68, 68, 0.4)', color: '#fca5a5', textDecoration: 'line-through' },
                          partiallyBooked: { backgroundColor: 'rgba(251, 191, 36, 0.3)', color: '#fcd34d' }
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-sm text-foreground/60 mb-2 block">Name *</label><input type="text" value={posForm.guest_name} onChange={(e) => setPosForm({ ...posForm, guest_name: e.target.value })} className="w-full input-default px-4" /></div>
                <div><label className="text-sm text-foreground/60 mb-2 block">Phone *</label><input type="tel" value={posForm.guest_phone} onChange={(e) => setPosForm({ ...posForm, guest_phone: e.target.value })} className="w-full input-default px-4" /></div>
              </div>
              <div><label className="text-sm text-foreground/60 mb-2 block">Email (optional)</label><input type="email" value={posForm.guest_email} onChange={(e) => setPosForm({ ...posForm, guest_email: e.target.value })} className="w-full input-default px-4" placeholder="guest@email.com" /></div>
              <div><label className="text-sm text-foreground/60 mb-2 block">Source</label>
                <div className="flex flex-wrap gap-2">
                  {['walk_in', 'phone', 'airbnb', 'booking_com'].map((s) => (
                    <button key={s} onClick={() => setPosForm({ ...posForm, source: s })} className={`px-3 py-1 text-sm transition-all ${posForm.source === s ? 'bg-gold-500 text-black' : 'bg-white/5 hover:bg-white/10'}`}>{s.replace('_', ' ')}</button>
                  ))}
                </div>
              </div>
              <div><label className="text-sm text-foreground/60 mb-2 block">Payment Method</label>
                <div className="flex flex-wrap gap-2">
                  {[{ id: 'cash', icon: Banknote, label: 'Cash' }, { id: 'card', icon: CreditCard, label: 'Card' }, { id: 'upi_counter', icon: QrCode, label: 'UPI' }].map((m) => (
                    <button key={m.id} onClick={() => setPosForm({ ...posForm, payment_method: m.id })} className={`flex items-center gap-2 px-4 py-2 text-sm transition-all ${posForm.payment_method === m.id ? 'bg-gold-500 text-black' : 'bg-white/5 hover:bg-white/10'}`}><m.icon size={16} />{m.label}</button>
                  ))}
                </div>
              </div>
              <div><label className="text-sm text-foreground/60 mb-2 block">Notes (optional)</label><textarea value={posForm.notes} onChange={(e) => setPosForm({ ...posForm, notes: e.target.value })} className="w-full input-default px-4 py-2" rows={2} placeholder="Any special requests or notes..." /></div>
              {posTotal > 0 && (
                <div className="p-4 bg-gold-500/10 border border-gold-500/20">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-foreground/70">Total ({posNights} night{posNights > 1 ? 's' : ''})</span>
                    <span className="text-gold-500 font-heading text-2xl">₹{posTotal.toLocaleString()}</span>
                  </div>
                  <div>
                    <label className="text-sm text-foreground/60 mb-1 block">Amount Received</label>
                    <input type="number" value={posForm.amount_paid} onChange={(e) => setPosForm({ ...posForm, amount_paid: Number(e.target.value) })} className="w-full input-default px-4" placeholder="0" />
                    {posForm.amount_paid > 0 && posForm.amount_paid < posTotal && (
                      <p className="text-orange-400 text-xs mt-1">Balance due: ₹{(posTotal - posForm.amount_paid).toLocaleString()}</p>
                    )}
                  </div>
                </div>
              )}
              <div className="flex gap-4 pt-4">
                <button onClick={() => setShowPOSModal(false)} className="btn-outline flex-1">Cancel</button>
                <button onClick={handlePOSSubmit} className="btn-primary flex-1" disabled={!posForm.room_id || !posForm.check_in || !posForm.check_out || !posForm.guest_name || !posForm.guest_phone}>Create Booking</button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Room Edit Modal */}
      {editingRoom && (
        <div className="fixed inset-0 z-50 bg-void/90 flex items-center justify-center p-4" onClick={() => { setEditingRoom(null); setIsNewRoom(false); }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-void-paper border border-gold-500/20 p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-2xl">{isNewRoom ? 'Add' : 'Edit'} <span className="text-gold-500">Room</span></h2>
              <button onClick={() => { setEditingRoom(null); setIsNewRoom(false); }}><X size={24} /></button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center"><h3 className="font-heading">Images</h3><button onClick={() => { setImagePickerTarget('room'); setShowImagePicker(true); }} className="btn-outline text-sm py-2"><Plus size={14} />Add</button></div>
                {editingRoom.images?.length === 0 ? <div className="p-8 border border-dashed border-gold-500/30 text-center text-foreground/50">No images</div> : (
                  <div className="space-y-2">
                    {editingRoom.images?.map((img, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 bg-void-subtle">
                        <img src={img} alt="" className="w-20 h-14 object-cover" />
                        <span className="flex-1 text-sm">{i === 0 && <span className="text-gold-500 text-xs mr-2">Main</span>}#{i + 1}</span>
                        <button onClick={() => moveImage(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30">↑</button>
                        <button onClick={() => moveImage(i, 1)} disabled={i === editingRoom.images.length - 1} className="p-1 disabled:opacity-30">↓</button>
                        <button onClick={() => removeImageFromRoom(i)} className="p-1 text-red-500"><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="text-sm text-foreground/60 mb-1 block">Name *</label><input type="text" value={editingRoom.name} onChange={(e) => setEditingRoom({ ...editingRoom, name: e.target.value })} className="w-full input-default px-4" /></div>
                  <div><label className="text-sm text-foreground/60 mb-1 block">Slug *</label><input type="text" value={editingRoom.slug} onChange={(e) => setEditingRoom({ ...editingRoom, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })} className="w-full input-default px-4" /></div>
                </div>
                <div><label className="text-sm text-foreground/60 mb-1 block">Category</label>
                  <select value={editingRoom.category} onChange={(e) => setEditingRoom({ ...editingRoom, category: e.target.value })} className="w-full input-default px-4">
                    <option value="heritage">Heritage</option><option value="modern">Modern</option><option value="unique">Unique</option>
                  </select>
                </div>
                <div><label className="text-sm text-foreground/60 mb-1 block">Short Desc</label><input type="text" value={editingRoom.short_description} onChange={(e) => setEditingRoom({ ...editingRoom, short_description: e.target.value })} className="w-full input-default px-4" /></div>
                <div><label className="text-sm text-foreground/60 mb-1 block">Description</label><textarea value={editingRoom.description} onChange={(e) => setEditingRoom({ ...editingRoom, description: e.target.value })} className="w-full input-default px-4 py-3" rows={3} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="text-sm text-foreground/60 mb-1 block">Price/Night</label><input type="number" value={editingRoom.price_per_night} onChange={(e) => setEditingRoom({ ...editingRoom, price_per_night: Number(e.target.value) })} className="w-full input-default px-4" /></div>
                  <div><label className="text-sm text-foreground/60 mb-1 block">Max Guests</label><input type="number" value={editingRoom.max_guests} onChange={(e) => setEditingRoom({ ...editingRoom, max_guests: Number(e.target.value) })} className="w-full input-default px-4" /></div>
                </div>
                <div><label className="text-sm text-foreground/60 mb-1 block">Original Price (optional — shows struck-through next to Price/Night when higher, for discounts)</label><input type="number" value={editingRoom.original_price || ''} onChange={(e) => setEditingRoom({ ...editingRoom, original_price: e.target.value ? Number(e.target.value) : null })} className="w-full input-default px-4" placeholder="Leave empty for no discount display" /></div>
                <div><label className="text-sm text-foreground/60 mb-1 block">Amenities</label><input type="text" value={editingRoom.amenities?.join(', ')} onChange={(e) => setEditingRoom({ ...editingRoom, amenities: e.target.value.split(',').map(a => a.trim()).filter(a => a) })} className="w-full input-default px-4" /></div>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={editingRoom.is_featured} onChange={(e) => setEditingRoom({ ...editingRoom, is_featured: e.target.checked })} className="accent-gold-500" /><span>Featured</span></label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={editingRoom.is_available} onChange={(e) => setEditingRoom({ ...editingRoom, is_available: e.target.checked })} className="accent-gold-500" /><span>Available</span></label>
                </div>
              </div>
            </div>
            <div className="flex gap-4 mt-6 pt-4 border-t border-gold-500/10">
              {!isNewRoom && <button onClick={handleRoomDelete} className="px-4 py-2 bg-red-500/20 text-red-500 hover:bg-red-500 hover:text-white"><Trash2 size={18} /></button>}
              <button onClick={() => { setEditingRoom(null); setIsNewRoom(false); }} className="btn-outline flex-1">Cancel</button>
              <button onClick={handleRoomSave} className="btn-primary flex-1">{isNewRoom ? 'Create' : 'Save'}</button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Booking Detail Popup */}
      <AnimatePresence>
        {selectedBookingDetail && <BookingDetailPopup booking={selectedBookingDetail} onClose={() => setSelectedBookingDetail(null)} />}
      </AnimatePresence>

      {/* Calendar Booking Detail Popup */}
      <AnimatePresence>
        {selectedCalendarBooking && <BookingDetailPopup booking={selectedCalendarBooking} onClose={() => setSelectedCalendarBooking(null)} />}
      </AnimatePresence>

      {/* Image Picker */}
      {showImagePicker && <ImagePickerModal onSelect={(url) => { if (imagePickerTarget === 'room') addImageToRoom(url); else if (imagePickerTarget === 'hero') addHeroImage(url); }} onClose={() => setShowImagePicker(false)} />}
    </div>
  );
};

export default AdminDashboard;
